import { Icon } from '@iconify/react';
import React, { useState } from 'react';

type AlertType = 'warning' | 'danger' | 'information' | 'primary';

interface AlertProps {
  type?: AlertType;
  isDismissable?: boolean;
  title?: React.ReactNode;
  body?: React.ReactNode;
  onClose?: () => void;
  className?: string;
  iconTitle?: string;
}

const typeStyles: Record<AlertType, { bg: string; border: string; text: string }> = {
  warning: { bg: 'bg-[#FFF9E7]', border: 'border-yellow-500', text: 'text-[#212529]' },
  danger: { bg: 'bg-red-100', border: 'border-red-500', text: 'text-red-700' },
  information: { bg: 'bg-blue-100', border: 'border-blue-500', text: 'text-blue-700' },
  primary: { bg: 'bg-primary-50', border: 'border-primary-500', text: 'text-[#212529]' },
};

const Alert: React.FC<AlertProps> = ({
  type = 'information',
  isDismissable = false,
  title,
  body,
  onClose,
  className = '',
  iconTitle = '',
}) => {
  const [visible, setVisible] = useState(true);
  if (!visible) return null;

  const styles = typeStyles[type];

  return (
    <div
      className={`${styles.bg} ${styles.text} rounded-lg p-4 ${className} ${type === 'primary' ? `border ${styles.border}` : null}`}
      role="alert"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {title && (
            <div className="flex items-center space-x-2 font-medium text-base">
              {type === 'warning' ? (
                <Icon icon="mdi:information-outline" className="h-5 w-5 text-[#FFBE0B]" />
              ) : iconTitle ? (
                <Icon icon={iconTitle} className="h-5 w-5" />
              ) : null}
              <span>{title}</span>
            </div>
          )}
        </div>

        {isDismissable && (
          <button
            type="button"
            aria-label="Close alert"
            onClick={() => {
              setVisible(false);
              onClose?.();
            }}
            className={`${styles.text} ml-4 inline-flex items-center justify-center p-1 rounded focus:outline-none`}
          >
            <Icon icon="mdi:close" className="h-5 w-5" />
          </button>
        )}
      </div>

      {body && <div className="mt-2">{body}</div>}
    </div>
  );
};

export default Alert;
